import React, {createRef, useContext, useEffect} from "react";
import {
  Box,
  BoxProps,
  Card,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import {
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  SearchCriteriaInstance,
  SearchCriteriaPayload,
  STEP_ORDERS,
  STEPS,
  useStep,
  validateStepOutput,
} from "../../contexts/OperatingTestContext";
import {MODEL_BG_COLOR} from "../../../../constants/work";
import {
  FindModelResponse,
  FrictionRegionInfo,
  Grasp2pInfo,
  pickGrasp2pRegionTypeLabel
} from "../../../../types/pj1dbApiHandling";
import {handleInputChange} from "../../../../utils/formUtil";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";

import {roundNum} from "../../../../utils/numberUtil";
import {pickGrasp2p} from "../../../../utils/handselectionUtil";
import {ArmSelectionField} from "../forms/ArmSelectionField";
import {ControlState, useControlState, useThree} from "../3d/View3D";
import {useHandlingWorkModel} from "../../hooks/useHandlingWorkModel";
import {MaterialTypeField} from "../3d/controlFields/MaterialTypeField";
import {PerspectiveField} from "../3d/controlFields/PerspectiveField";
import {LookAtField} from "../3d/controlFields/LookAtField";
import {GridSizeField} from "../3d/controlFields/GridSizeField";
import WorkCandidateModel3D from "../3d/WorkCandidateModel3D";
import SearchCriteriaInstanceGroup from "../forms/SearchCriteriaInstanceGroup";
import HandPowerSourceField from "../forms/HandPowerSourceField";
import {useArm} from "../../hooks/useArm";
import TitleWithBackButton from "../utils/TitleWithBackButton";
import {FetchMessage} from "../../../../hooks/useFetchAllQuery";
import useNotification from "../../../../utils/notificationUtil";

export default function SearchCriteriaAdditionalStep() {
  if (!useIsShow()) {
    throw new Error('SearchCriteriaAdditionalStep is not shown');
  }

  const formRef = createRef<HTMLFormElement>();
  const {state, dispatch} = useContext(DataStoreContext);
  const {errorMsg} = useNotification();
  const [, setStep] = useStep();
  const three = useThree();
  const {controlState, setControlState} = useControlState();
  const {searchStatus} = useHandlingWorkModel({
    enableAutoFetch: true,
    instanceId: state.workInstanceStep.selectedWorkInstance?.attr["instance-id"] ?? '',
    initialStatus: state.searchCriteriaStep.searchStatus,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
        payload: {
          workModel: newData,
          searchStatus: newStatus,
          selectedDirectionIndex: 0,
        } satisfies SearchCriteriaPayload
      });
    }
  })
  const {fetchAll: fetchArm} = useArm({
    keyword: state.searchCriteriaStep.armSearchCond.keyword,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
        payload: {
          arms: newData,
          armSearchStatus: newStatus,
        } satisfies SearchCriteriaPayload
      });
    }
  })
  const selectedStyle: BoxProps['sx'] = {
    outlineColor: (theme) => theme.palette.primary.light,
    outlineStyle: "solid",
    outlineWidth: 6,
    borderRadius: 1,
  };
  const selectedData = pickSelectedDirectionData({
    directionIndex: state.searchCriteriaStep.selectedDirectionIndex,
    workModel: state.searchCriteriaStep.workModel,
  });
  const setFormData = (params: { objectId: string, info: Grasp2pInfo }) => {
    const calculated = calcSearchCriteria({
      info: params.info,
      region: state.searchCriteriaStep.workModel?.regionList.find((r) => r.id === params.objectId),
      direction: selectedData.direction,
    });
    dispatch({
      type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
      payload: {
        selectedObjectId: params.objectId,
        searchCriteria: {
          ...state.searchCriteriaStep.searchCriteria,
          rangeOpen: calculated.rangeOpen,
          rangeClose: calculated.rangeClose,
          forceMin: calculated.forceMin,
          forceMax: calculated.forceMax,
          stroke: calculated.stroke,
          payload: calculated.payload,
          flexibleWidth: calculated.flexibleWidth,
        }
      } satisfies SearchCriteriaPayload
    });
  }
  useEffect(() => {
    console.log('SearchCriteriaAdditionalStep: useEffect');
    // 値が全て空の場合、初期値を設定する
    const grasp2p = selectedData.grasp2p.find(() => true);
    if (grasp2p !== undefined && Object.values(state.searchCriteriaStep.searchCriteria).every((v) => v === '' || v === undefined)) {
      setFormData(grasp2p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData]);
  three.control?.selectObject(state.searchCriteriaStep.selectedObjectId ?? undefined);

  return (
    <Stack gap={4}>
      <TitleWithBackButton
        title={`対象物名：${state.workClassStep.selectedWorkClass?.attr.nameJp ?? ''}`}
        onClick={() => {
          setStep(STEP_ORDERS[STEPS.SEARCH_CRITERIA_ADDITIONAL].PREV);
        }}
        prevText='サンプル選択に戻る'
      />

      <FetchMessage
        data={
          state.searchCriteriaStep.workModel
            ? [state.searchCriteriaStep.workModel]
            : []
        }
        status={searchStatus}
        isEmpty={(items) => items.flatMap(item => item.gravityWithDirections ?? []).length === 0}
      />

      {(state.searchCriteriaStep.workModel?.gravityWithDirections?.length ?? 0) > 0 && (
        <Box>
          <Typography variant='body2'>
            設置姿勢を選択してください。その後把持候補リストの行をクリックして把持部を選択してください。
            <br/>※設置姿勢や把持候補領域は1つしかない場合もあります。
          </Typography>
          <Box display='flex' overflow='scroll' p={2} gap={4}>
            {state.searchCriteriaStep.workModel?.gravityWithDirections?.map((direction, index) => (
              <Box
                key={direction.thumbnail}
                sx={{
                  outlineColor: MODEL_BG_COLOR,
                  outlineStyle: "solid",
                  outlineWidth: 1,
                  borderRadius: 1,
                  ...(index === state.searchCriteriaStep.selectedDirectionIndex ? selectedStyle : {})
                }}
                width={200}
                height={200}
                onClick={() => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                    payload: {
                      selectedDirectionIndex: index,
                      selectedObjectId: pickSelectedDirectionData({
                        directionIndex: index,
                        workModel: state.searchCriteriaStep.workModel
                      }).grasp2p[0]?.objectId ?? null,
                    } satisfies SearchCriteriaPayload
                  });
                }}
              >
                <img
                  src={direction.thumbnail}
                  alt="thumbnail"
                  width={200}
                  height={200}
                  style={{objectFit: 'scale-down', backgroundColor: MODEL_BG_COLOR}}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}


      {selectedData.direction && (
        <Card sx={{p: 4}}>
          <Box display='flex' gap={6} flexWrap='wrap' width='100%'>
            <Stack gap={1}>
              <Typography variant="body2">※ モデルはイメージです。実際とは異なる場合があります。</Typography>
              {state.searchCriteriaStep.workModel && (
                <WorkCandidateModel3D
                  three={three}
                  model={state.searchCriteriaStep.workModel}
                  gravity={selectedData.direction.gravity}
                  onObjectSelected={(object) => {
                    dispatch({
                      type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                      payload: {
                        selectedObjectId: object?.name ?? null,
                      } satisfies SearchCriteriaPayload
                    })
                  }}
                />
              )}
            </Stack>

            <Stack gap={6}>
              <Stack gap={2}>
                <MaterialTypeField control={three.control} value={controlState.materialType}
                                   onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}/>
                <PerspectiveField control={three.control} value={controlState.perspective}
                                  onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}/>
                <LookAtField control={three.control} value={controlState.lookAtType}
                             onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}/>
                <GridSizeField control={three.control} value={controlState.gridSize}
                               onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}/>
              </Stack>

              <Stack gap={1}>
                <Typography variant='body1'>把持候補リスト</Typography>
                <TableContainer component={Paper} sx={{width: 'fit-content', maxWidth: "100%"}}>
                  <Table size="small">
                    <TableHead sx={{bgcolor: "#EEE"}}>
                      <TableRow>
                        <TableCell align='center' sx={{px: 1, py: 0.5}}>No</TableCell>
                        <TableCell align='center' sx={{px: 1, py: 0.5}}>把持候補領域タイプ</TableCell>
                        <TableCell align='center' sx={{px: 1, py: 0.5}}>必要把持力</TableCell>
                        <TableCell align='center' sx={{px: 1, py: 0.5}}>必要把持幅</TableCell>
                        {state.workClassStep.selectedWorkClass?.attr.softMaterial === true && (
                          <TableCell align='center' sx={{px: 1, py: 0.5}}>柔軟物把持幅</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedData.grasp2p.map(({objectId, info}, infoIndex) => {
                        const calculated = calcSearchCriteria({
                          info,
                          region: state.searchCriteriaStep.workModel?.regionList.find((r) => r.id === objectId),
                          direction: selectedData.direction,
                        });
                        return (
                          <TableRow
                            key={`${objectId}-${info.id}`}
                            sx={{
                              '&:last-child td, &:last-child th': {border: 0},
                              bgcolor: state.searchCriteriaStep.selectedObjectId === objectId ? "#deeeff" : undefined,
                            }}
                            onClick={() => {
                              setFormData({objectId, info});
                            }}
                          >
                            <TableCell align='center' sx={{px: 1, py: 0.5}}>{infoIndex + 1}</TableCell>
                            <TableCell align='center'
                                       sx={{px: 1, py: 0.5}}>{pickGrasp2pRegionTypeLabel(objectId)}</TableCell>
                            <TableCell align='center' sx={{px: 1, py: 0.5}}
                                       dangerouslySetInnerHTML={{__html: `${roundNum(info.requiredForce ?? 0)} N`}}/>
                            <TableCell align='center' sx={{px: 1, py: 0.5}}
                                       dangerouslySetInnerHTML={{__html: `${roundNum(info.position?.radius ? info.position.radius * 1000 * 2 : 0)} mm`}}/>
                            {state.workClassStep.selectedWorkClass?.attr.softMaterial === true && (
                              <TableCell align='center' sx={{px: 1, py: 0.5}}
                                         dangerouslySetInnerHTML={{__html: `${calculated.flexibleWidth} mm`}}/>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Stack>

            <form ref={formRef} style={{width: '100%'}}>
              <Stack gap={2} width='100%'>
                <Typography variant='h6'>ハンド検索条件</Typography>

                <SearchCriteriaInstanceGroup
                  isClear={false}
                  inputs={state.searchCriteriaStep.searchCriteria}
                  onChange={(value) => {
                    console.log('SearchCriteriaInstanceGroup.onChange', value);
                    dispatch({
                      type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                      payload: {
                        searchCriteria: value,
                      } satisfies SearchCriteriaPayload
                    });
                  }}
                  softMaterial={state.workClassStep.selectedWorkClass?.attr.softMaterial ?? false}
                />

                <HandPowerSourceField
                  isClear={false}
                  inputs={state.searchCriteriaStep.handPowerSourceInputs}
                  onChange={(value) => {
                    dispatch({
                      type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                      payload: {
                        handPowerSourceInputs: value,
                      } satisfies SearchCriteriaPayload
                    });
                  }}
                />

                <ArmSelectionField
                  arms={state.searchCriteriaStep.arms.map((arm) => Object.values(arm)).flat()}
                  onChange={(arm) => {
                    dispatch({
                      type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                      payload: {
                        selectedArm: arm,
                      } satisfies SearchCriteriaPayload
                    });
                  }}
                  selectedArm={state.searchCriteriaStep.selectedArm}
                  searchStatus={state.searchCriteriaStep.armSearchStatus}
                  searchCond={state.searchCriteriaStep.armSearchCond}
                  onChangeSearchCond={(data) => {
                    dispatch({
                      type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                      payload: {
                        armSearchCond: data,
                      } satisfies SearchCriteriaPayload
                    });
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    fetchArm().catch(console.error);
                  }}
                />

                <Box display='flex' justifyContent='end'>
                  <Pjdb1Button
                    label='検索条件決定'
                    variant='contained'
                    color='primary'
                    onClick={(e) => {
                      e.preventDefault();
                      if (!formRef.current?.reportValidity()) {
                        errorMsg("入力内容に誤りがあります。");
                        return;
                      }
                      if (!state.searchCriteriaStep.workModel) {
                        throw new Error("モデルが選択されていません。");
                        return;
                      }
                      if (selectedData.direction === null) {
                        throw new Error("モデルの向きが選択されていません。");
                        return;
                      }
                      dispatch({
                        type: CONTEXT_ACTION_TYPE.SEARCH_CRITERIA,
                        payload: {
                          searchCriteriaList: [
                            ...state.searchCriteriaStep.searchCriteriaList,
                            {
                              // バリデーションされている前提
                              ...state.searchCriteriaStep.searchCriteria as SearchCriteriaInstance,
                              softMaterial: state.workClassStep.selectedWorkClass?.attr.softMaterial ?? false,
                              deformable: state.workClassStep.selectedWorkClass?.attr.deformable ?? false,
                              instanceID: state.workInstanceStep.selectedWorkInstance?.attr["instance-id"] ?? '',
                              direction: selectedData.direction,
                              flexibleWidth: roundNum((state.searchCriteriaStep.workModel?.regionList.find((r) => r.id === state.searchCriteriaStep.selectedObjectId)?.flexibleWidth ?? 0) * 1000).toString(),
                            }
                          ],
                        } satisfies SearchCriteriaPayload
                      })
                    }}/>
                </Box>
              </Stack>
            </form>
          </Box>
        </Card>
      )}
    </Stack>
  );
}

function useIsShow() {
  const {state} = useContext(DataStoreContext);
  const [step] = useStep();

  return STEPS.SEARCH_CRITERIA_ADDITIONAL === step &&
    validateStepOutput(state, STEP_ORDERS[STEPS.SEARCH_CRITERIA_ADDITIONAL].PREV);
}

interface PickSelectedDirectionDataParams {
  directionIndex: number;
  workModel: FindModelResponse | null;
}

function pickSelectedDirectionData(params: PickSelectedDirectionDataParams) {
  return {
    direction: params.workModel?.gravityWithDirections?.[params.directionIndex] ?? null,
    grasp2p: pickGrasp2p(params.directionIndex, params.workModel?.grasp2p ?? {}),
  }
}

function calcSearchCriteria({info, region, direction}: {
  info: Grasp2pInfo | null,
  region?: FrictionRegionInfo,
  direction: Required<FindModelResponse>['gravityWithDirections'][number] | null,
}) {
  // APIで取得される数値の単位は m だが、UI表示、APIへのインプットは mm にする必要がある
  const radius = info?.position?.radius ? info.position.radius * 1000 : 0;
  // 初期値: 閉時幅 = 半径(radius) × 2 - 2mm
  const rangeClose = roundNum(radius * 2 - (radius === 0 ? 0 : 2));
  // 初期値: 開時幅 = 閉時幅 + 10mm
  const rangeOpen = roundNum(rangeClose + 10);

  return {
    rangeOpen: rangeOpen.toString(),
    rangeClose: rangeClose.toString(),
    // 初期値: ストローク = 開時幅 - 閉時幅
    stroke: String(roundNum(rangeOpen - rangeClose)),
    // 初期値: 把持力下限 = 必要把持力
    forceMin: roundNum(info?.requiredForce ?? 0).toString(),
    // 初期値: なし
    forceMax: '',
    // 初期値: 可搬重量 = 重量
    payload: roundNum(direction?.gravity.weight ?? 0).toString(),
    flexibleWidth: roundNum((region?.flexibleWidth ?? 0) * 1000).toString(),
  }
}
